import { useState, type FormEvent } from 'react';
import { personFacingMessage } from '~/lib/query-client';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { Skeleton } from '~/components/ui/skeleton';
import { useProBuilderApp, useProBuilderProjects, type ProBuilderProject } from '~/hooks/queries/use-pro-builder';
import { useBindProBuilderApp, type ProBuilderBindInput } from '~/hooks/mutations/use-pro-builder';
import { canWorkOnProject, projectDisplayName } from './pro-builder-app-rules';
import { useProBuilderBusyReason } from './pro-builder-busy';
import { appStatusLine } from './pro-builder-publish-dialog';
import { useProBuilderPickerStore } from './pro-builder-picker-store';

export const PICKER_HEADING = 'Pick an app to work on';
export const PICKER_EMPTY = 'No apps yet — name your first one.';
export const PICKER_LIST_ERROR = "Couldn't read your apps. Try again.";
export const PICKER_BIND_ERROR = 'Could not switch to that app. Try again.';
export const NEW_APP_HEADING = 'New app';
export const NEW_APP_ERROR = 'Could not start that app. Try again.';

/**
 * The "name a new app" field and its Start button — one form, two places: the
 * first-time picker below its list, and the header's "New app" view. The two
 * are never on screen together (the header hides New while the picker shows),
 * so the field's test ids stay unique.
 */
function NewAppForm({
  busy,
  onStart,
  label,
  autoFocus = false,
}: {
  busy: boolean;
  onStart: (name: string) => void;
  label: string;
  autoFocus?: boolean;
}) {
  const [name, setName] = useState('');
  const trimmed = name.trim();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (trimmed && !busy) onStart(trimmed);
  };
  return (
    <form onSubmit={submit} className="flex items-end gap-2">
      <label className="flex min-w-0 flex-1 flex-col gap-1.5 text-sm font-medium text-foreground">
        {label}
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Name"
          data-testid="pro-builder-new-app-name"
          disabled={busy}
          autoFocus={autoFocus}
        />
      </label>
      <Button type="submit" size="sm" disabled={busy || trimmed === ''} data-testid="pro-builder-new-app-start">
        Start
      </Button>
    </form>
  );
}

/**
 * The picker (FRONT-3077): pure — state in, markup out. Shown only while the
 * window is bound to nothing; moving between apps once one is bound is the
 * header's app switcher, and starting another is the header's New. Rows are
 * the folders in the person's projects (brought-in apps included). A row's
 * blocked reason is about PUBLISHING (archived, stale copy, builder busy…), so
 * it is shown as an informational line and "Work on this" stays on; only a
 * folder name the server cannot open is disabled (`canWorkOnProject`).
 */
export function ProBuilderAppPickerView({
  projects,
  busy,
  error,
  onPick,
  onStart,
}: {
  projects: ProBuilderProject[];
  busy: boolean;
  /** The refusal (or failure) from the last attempt, shown next to the controls. */
  error: string | null;
  onPick: (folder: string) => void;
  onStart: (name: string) => void;
}) {
  return (
    <div data-testid="pro-builder-app-picker" className="flex flex-col gap-5">
      <h2 className="text-base font-semibold text-foreground">{PICKER_HEADING}</h2>
      {projects.length === 0 ? (
        <p data-testid="pro-builder-app-picker-empty" className="text-sm text-muted-foreground">
          {PICKER_EMPTY}
        </p>
      ) : (
        <ul className="flex flex-col divide-y divide-border">
          {projects.map((p) => (
            <li
              key={p.name}
              data-testid="pro-builder-app-option"
              data-folder={p.name}
              className="flex flex-col gap-1.5 py-3 first:pt-0 last:pb-0"
            >
              <div className="flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-foreground">{projectDisplayName(p)}</div>
                  <div className="text-xs text-muted-foreground">{appStatusLine(p.app)}</div>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={busy || !canWorkOnProject(p)}
                  onClick={() => onPick(p.name)}
                  data-testid="pro-builder-app-pick"
                >
                  Work on this
                </Button>
              </div>
              {p.blockedReason !== null && (
                <p data-testid="pro-builder-app-blocked" className="text-xs text-muted-foreground">
                  {p.blockedReason}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
      <NewAppForm busy={busy} onStart={onStart} label="New app" />
      {error !== null && (
        <p role="alert" data-testid="pro-builder-app-picker-error" className="text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * The header's "New app" view (FRONT-3077 header round), shown in place of the
 * terminal while the bound app stays bound: the picker's own name field and
 * Start, plus Cancel back to the terminal (which reattaches the same shell).
 * Pure — state in, markup out.
 */
export function ProBuilderNewAppView({
  busy,
  error,
  onStart,
  onCancel,
}: {
  busy: boolean;
  /** The refusal (or failure) from the last Start, shown next to the controls. */
  error: string | null;
  onStart: (name: string) => void;
  onCancel: () => void;
}) {
  return (
    <div data-testid="pro-builder-new-app-view" className="flex flex-col gap-5">
      <h2 className="text-base font-semibold text-foreground">{NEW_APP_HEADING}</h2>
      <NewAppForm busy={busy} onStart={onStart} label="App name" autoFocus />
      {error !== null && (
        <p role="alert" data-testid="pro-builder-new-app-error" className="text-sm text-destructive">
          {error}
        </p>
      )}
      <div>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel} disabled={busy} data-testid="pro-builder-new-app-cancel">
          Cancel
        </Button>
      </div>
    </div>
  );
}

/** The connected "New app" view: Start binds a new folder by name (success leaves the view — `useBindProBuilderApp`). */
export function ProBuilderNewApp() {
  const bind = useBindProBuilderApp();
  const setCreating = useProBuilderPickerStore((s) => s.setCreating);
  // One busy decision for the New view's Start (the header shows the sentence): this bind pending, or any other switch / a publish of the bound app.
  const blocked = useProBuilderBusyReason(useProBuilderApp(true).data?.project?.name);
  return (
    <ProBuilderNewAppView
      busy={bind.isPending || blocked !== null}
      error={bind.isError ? personFacingMessage(bind.error, NEW_APP_ERROR) : null}
      onStart={(name) => bind.mutate({ name })}
      onCancel={() => setCreating(false)}
    />
  );
}

/** The connected picker: the project list read and the bind mutation. Loading, error and list are separate branches. */
export function ProBuilderAppPicker() {
  const projects = useProBuilderProjects(true);
  const bind = useBindProBuilderApp();
  const data = projects.data;

  if (projects.isError && !data) {
    return (
      <div className="flex flex-col items-start gap-2">
        <p role="alert" data-testid="pro-builder-app-picker-list-error" className="text-sm text-destructive">
          {personFacingMessage(projects.error, PICKER_LIST_ERROR)}
        </p>
        <Button type="button" size="sm" variant="outline" onClick={() => void projects.refetch()}>
          Try again
        </Button>
      </div>
    );
  }
  if (projects.isPending || !data) {
    return <Skeleton data-testid="pro-builder-app-picker-loading" className="h-32 w-full rounded-lg" />;
  }
  const run = (input: ProBuilderBindInput) => bind.mutate(input);
  return (
    <ProBuilderAppPickerView
      projects={data.projects}
      busy={bind.isPending}
      error={bind.isError ? personFacingMessage(bind.error, PICKER_BIND_ERROR) : null}
      onPick={(folder) => run({ folder })}
      onStart={(name) => run({ name })}
    />
  );
}
